import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';

interface Resource<T> {
  data: T | null;
  /**
   * Nothing to show yet — a first load, or a switch to a different resource.
   *
   * NOT "a request is in flight". Re-reading the resource already on screen
   * leaves this false, which is what stops `if (loading) return <Loading />`
   * tearing a page down every time something on it is saved. Use `refreshing`
   * if you want to show that a background read is happening.
   */
  loading: boolean;
  /** A read is in flight, including one that is only refreshing what is
   *  already shown. Almost always the wrong thing to gate rendering on. */
  refreshing: boolean;
  error: string | null;
  /** HTTP status behind `error`: 403 is a permissions problem, 0 means the API
   *  was never reached, 5xx means it failed. Callers need the difference to
   *  avoid telling someone to request access when the server is simply down. */
  errorStatus: number | null;
  reload: () => void;
  /** Optimistic local update — avoids a round trip after a small edit. */
  set: (updater: (current: T) => T) => void;
}

/**
 * Loads `path` and re-loads whenever it changes or `reload()` is called.
 *
 * A RELOAD IS NOT A LOAD, and the difference is the whole of what `loading`
 * means. Almost every page in this app opens with
 *
 *     if (resource.loading) return <Loading />;
 *
 * and almost every page also calls `reload()` after a save, to pick up
 * whatever the server recomputed. While this hook set `loading` on both, that
 * pair tore the page down and replaced it with a spinner on EVERY save — the
 * scroll position back to the top, anything open closed, focus gone. On the
 * OML assessment, where somebody answers 110 questions in a sitting, it read
 * as the page reloading on every click, which is how it was finally reported.
 *
 * So `loading` now means "there is nothing to show", and a re-read of the path
 * already on screen leaves it false: the page keeps rendering what it has and
 * the new data swaps in when it lands.
 *
 * A CHANGE OF PATH IS UNTOUCHED, deliberately. That IS a different resource,
 * the data on screen belongs to something else, and every page's spinner is
 * the right answer. It also keeps the search-as-you-type pickers working —
 * their path changes per keystroke, and they rely on `loading` to say
 * "Searching…" — which is exactly what a rule about in-flight requests rather
 * than about paths would have broken.
 */
export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);

  /* Guards against a slow first response overwriting a fast second one when
     the path changes mid-flight. */
  const latest = useRef(0);

  /* Which path the current `data` belongs to, so a re-read of it can be told
     from a move to a different one. Set only on success: a path that failed
     has nothing on screen, and the next attempt at it is still a first load. */
  const loadedPath = useRef<string | null>(null);

  useEffect(() => {
    if (path === null) {
      setData(null);
      loadedPath.current = null;
      setLoading(false);
      return;
    }
    const ticket = ++latest.current;
    /* The one line this whole hook's comment is about. */
    if (loadedPath.current !== path) setLoading(true);
    setRefreshing(true);
    setError(null);
    setErrorStatus(null);

    api
      .get<T>(path)
      .then((result) => {
        if (ticket !== latest.current) return;
        setData(result);
        loadedPath.current = path;
      })
      .catch((err: unknown) => {
        if (ticket !== latest.current) return;
        setError(err instanceof ApiError ? err.message : 'Something went wrong loading this page.');
        setErrorStatus(err instanceof ApiError ? err.status : null);
      })
      .finally(() => {
        if (ticket !== latest.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback((updater: (current: T) => T) => {
    setData((current) => (current === null ? current : updater(current)));
  }, []);

  return { data, loading, refreshing, error, errorStatus, reload, set };
}

/* Results held by useHeldResource, by path, for the life of the browser tab. */
const held = new Map<string, { data: unknown; at: number }>();

/**
 * Like useResource, but a result is KEPT: going back to a page, or moving
 * between slides of a review, shows what was already read — instantly, with
 * no request — until `refresh()` asks for it again.
 *
 * For the live Autotask reads (reports, the review's ticket slides, a
 * client's assets), where every read costs a round trip to Autotask and its
 * hourly API allowance, and the numbers do not need to move while somebody is
 * looking at them. `refresh()` also passes `refresh=1`, so the API skips its
 * own short cache and goes back to Autotask.
 *
 * Held in memory, so a full browser reload starts fresh.
 */
export function useHeldResource<T>(path: string | null) {
  const cached = path ? (held.get(path) as { data: T; at: number } | undefined) : undefined;
  const [data, setData] = useState<T | null>(cached?.data ?? null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(cached?.at ?? null);
  const [loading, setLoading] = useState(path !== null && !cached);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  /* The path a refresh was asked for. Only that path is re-read; a move to
     another path afterwards starts from whatever is held for it. */
  const forcePath = useRef<string | null>(null);
  const latest = useRef(0);

  useEffect(() => {
    if (path === null) {
      setData(null);
      setLoading(false);
      return;
    }
    const hit = held.get(path) as { data: T; at: number } | undefined;
    const forced = forcePath.current === path;
    forcePath.current = null;
    if (hit && !forced) {
      setData(hit.data);
      setFetchedAt(hit.at);
      setLoading(false);
      setError(null);
      return;
    }
    const ticket = ++latest.current;
    if (!hit) setLoading(true);
    setRefreshing(true);
    setError(null);
    const url = forced ? `${path}${path.includes('?') ? '&' : '?'}refresh=1` : path;
    api
      .get<T>(url)
      .then((result) => {
        if (ticket !== latest.current) return;
        const at = Date.now();
        held.set(path, { data: result, at });
        setData(result);
        setFetchedAt(at);
      })
      .catch((err: unknown) => {
        if (ticket !== latest.current) return;
        setError(err instanceof ApiError ? err.message : 'Something went wrong loading this page.');
      })
      .finally(() => {
        if (ticket !== latest.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  }, [path, nonce]);

  const refresh = useCallback(() => {
    forcePath.current = path;
    setNonce((n) => n + 1);
  }, [path]);
  return { data, loading, refreshing, error, fetchedAt, refresh };
}

/**
 * Debounced auto-save for inline editing.
 *
 * The V/TO and charter pages are long text forms; saving on every keystroke
 * would hammer the API, and saving only on blur loses work when a tab closes.
 * This settles at `delay` after typing stops.
 */
export function useAutoSave<T>(save: (value: T) => Promise<unknown>, delay = 800) {
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const trigger = useCallback(
    (value: T) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setStatus('saving');
        saveRef
          .current(value)
          .then(() => {
            setStatus('saved');
            setTimeout(() => setStatus('idle'), 1800);
          })
          .catch(() => setStatus('error'));
      }, delay);
    },
    [delay],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { trigger, status };
}

/**
 * Fetches an image from the API and hands back an object URL.
 *
 * A plain `<img src="/api/…">` carries no Authorization header and would 401 —
 * the same problem the job description viewer has. So the bytes are fetched
 * through lib/api and wrapped in a blob URL. Revoked on unmount, because a
 * blob URL is a leak until it is.
 *
 * Lives here rather than beside its first caller because it now has two: the
 * CS tool logos and the workflow rule screenshots in the manual.
 */
export function useAuthedImage(path: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;

    api
      .getBlob(path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      /* A missing image is not worth an error banner — every caller has
         something to show in its place. */
      .catch(() => setUrl(null));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return url;
}
